export const DIFFICULTY_LEVELS = ['easy', 'medium', 'hard'] as const;

export type DifficultyBucket = (typeof DIFFICULTY_LEVELS)[number];

export const DIFFICULTY_LABELS: Record<DifficultyBucket, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      toFiniteNumber(record.value) ??
      toFiniteNumber(record.score) ??
      toFiniteNumber(record.level) ??
      null
    );
  }
  return null;
};

export const getDifficultyValue = (difficulty: unknown): number | null => {
  if (!difficulty || typeof difficulty !== 'object') return toFiniteNumber(difficulty);
  const record = difficulty as Record<string, unknown>;

  return (
    toFiniteNumber(record.overall) ??
    toFiniteNumber(record.overall_difficulty) ??
    toFiniteNumber(record.difficulty) ??
    toFiniteNumber(record.level) ??
    toFiniteNumber(record.value) ??
    null
  );
};

export const getDifficultyBucket = (difficulty: unknown): DifficultyBucket | null => {
  const value = getDifficultyValue(difficulty);
  if (value === null) return null;
  if (value <= 2) return 'easy';
  if (value <= 4) return 'medium';
  return 'hard';
};

export const matchesDifficultyBucket = (
  difficulty: unknown,
  selected: DifficultyBucket | null,
): boolean => {
  if (!selected) return true;
  const bucket = getDifficultyBucket(difficulty);
  return bucket === selected || (bucket === null && selected === 'easy');
};
